import React, { useEffect, useState, useCallback } from "react";
import * as func from "../../api/apiHelper";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarXmark,
  faSpinner,
  faBell,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";

const PACK_COLORS = {
  single_unlock: "bg-blue-100 text-blue-700",
  spark_bundle:  "bg-cyan-100 text-cyan-700",
  premium:       "bg-purple-100 text-purple-700",
  elite:         "bg-yellow-100 text-yellow-700",
  annual:        "bg-green-100 text-green-700",
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const daysUntil = (d) => {
  if (!d) return null;
  const diff = new Date(d) - new Date();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

const SubscriptionExpiry = () => {
  const [loading, setLoading] = useState(true);
  const [packs, setPacks] = useState([]);
  const [days, setDays] = useState(7);
  const [sendingId, setSendingId] = useState(null);
  const [extendingId, setExtendingId] = useState(null);
  const [extendDays, setExtendDays] = useState({});

  const fetchExpiring = useCallback(async (d) => {
    setLoading(true);
    try {
      const res = await func.get(`mst-admin/user-packs/expiring?days=${d}`);
      if (res?.status === 200 && res.data?.success) {
        setPacks(res.data.packs || []);
      }
    } catch {
      toast.error("Failed to load expiring subscriptions");
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchExpiring(days); }, [fetchExpiring, days]);

  const handleSendReminder = async (pack) => {
    setSendingId(pack._id);
    try {
      const res = await func.post("mst-admin/notifications/broadcast", {
        title: "Your subscription is expiring soon!",
        body: `Your ${pack.packType} pack expires on ${fmtDate(pack.packExpiry)}. Renew now to keep enjoying premium features.`,
        audience: { segment: "ALL", userIds: [pack.userId] },
      });
      if (res?.status === 200 && res.data?.success) {
        toast.success("Reminder sent!");
      } else {
        toast.error("Failed to send reminder");
      }
    } catch {
      toast.error("Failed to send reminder");
    }
    setSendingId(null);
  };

  const handleExtend = async (pack) => {
    const extra = Number(extendDays[pack._id] ?? 7);
    if (!extra || extra < 1) {
      toast.warn("Enter extension days first");
      return;
    }
    if (!window.confirm(`Extend ${pack.packType} for this user by ${extra} days?`)) return;
    setExtendingId(pack._id);
    try {
      const currentExpiry = pack.packExpiry ? new Date(pack.packExpiry) : new Date();
      const newExpiry = new Date(currentExpiry.getTime() + extra * 24 * 60 * 60 * 1000);
      const durationFromNow = Math.ceil((newExpiry - new Date()) / (1000 * 60 * 60 * 24));
      const res = await func.post(`mst-admin/user-packs/${pack.userId}/grant`, {
        packType: pack.packType,
        durationDays: durationFromNow,
        note: `Admin extended by ${extra} days`,
      });
      if (res?.status === 200 && res.data?.success) {
        toast.success(`Extended by ${extra} days`);
        fetchExpiring(days);
      } else {
        toast.error(res?.data?.message || "Extend failed");
      }
    } catch {
      toast.error("Extend failed");
    }
    setExtendingId(null);
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <FontAwesomeIcon icon={faCalendarXmark} className="text-2xl text-red-500" />
          <h1 className="text-2xl font-bold text-gray-800">Subscription Expiry</h1>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Expiring within</span>
          <select
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          >
            {[3, 7, 14, 30].map(d => <option key={d} value={d}>{d} days</option>)}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
        <div className="p-4 border-b">
          <p className="font-semibold text-gray-700">
            {packs.length} subscription{packs.length !== 1 ? "s" : ""} expiring in {days} days
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <FontAwesomeIcon icon={faSpinner} spin className="text-2xl text-gray-400" />
          </div>
        ) : packs.length === 0 ? (
          <p className="text-center text-gray-400 py-10">No subscriptions expiring in this window.</p>
        ) : (
          <div className="divide-y">
            {packs.map((pack) => {
              const remaining = daysUntil(pack.packExpiry);
              return (
                <div key={pack._id} className="px-5 py-4 flex items-center gap-4 hover:bg-gray-50">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${PACK_COLORS[pack.packType] || "bg-gray-100 text-gray-600"}`}>
                        {pack.packType}
                      </span>
                      <span className={`text-xs font-semibold ${remaining <= 3 ? "text-red-600" : "text-yellow-600"}`}>
                        {remaining != null ? `${remaining}d left` : ""}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 font-mono">{pack.userId?.toString().slice(-10)}</p>
                    <p className="text-xs text-gray-400 mt-0.5">Expires: {fmtDate(pack.packExpiry)}</p>
                  </div>

                  {/* Extend */}
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      className="w-16 border border-gray-200 rounded px-2 py-1 text-xs text-center"
                      placeholder="days"
                      value={extendDays[pack._id] ?? ""}
                      onChange={(e) => setExtendDays(d => ({ ...d, [pack._id]: e.target.value }))}
                    />
                    <button
                      onClick={() => handleExtend(pack)}
                      disabled={extendingId === pack._id}
                      className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 disabled:opacity-50"
                    >
                      <FontAwesomeIcon icon={extendingId === pack._id ? faSpinner : faArrowRight} spin={extendingId === pack._id} />
                      Extend
                    </button>
                  </div>

                  {/* Send reminder */}
                  <button
                    onClick={() => handleSendReminder(pack)}
                    disabled={sendingId === pack._id}
                    className="flex items-center gap-2 px-3 py-1.5 text-xs bg-orange-50 border border-orange-200 text-orange-700 rounded-lg hover:bg-orange-100 disabled:opacity-50"
                  >
                    <FontAwesomeIcon icon={sendingId === pack._id ? faSpinner : faBell} spin={sendingId === pack._id} />
                    Send Reminder
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionExpiry;
